import React, { useCallback, useEffect, useMemo, useState } from 'react';
import aiAutomationApi from '../../utils/aiAutomationApi';

/**
 * AISetupList — lists a job's durable, reusable AI interview setups (multiple can be active at once),
 * each with its own per-setup stats (Invited / Booked / Available) and lock state.
 *
 * A setup is LOCKED once ≥1 candidate has been invited: its config is immutable, so the only actions
 * are "Use this setup" (invite more candidates into it / generate more slots) and "View details".
 * An unlocked setup can still be edited. "Create New Setup" builds a fresh sibling.
 *
 * Presentational + self-contained: it fetches GET /interviews/search/{searchId}/setups and calls back
 * to the parent for the actual create/use/edit flows (which own the scheduling handoff).
 */
const StatPill = ({ label, value, isDark, tone }) => {
  const tones = {
    invited: isDark ? 'bg-indigo-900/40 text-indigo-200' : 'bg-indigo-50 text-indigo-700',
    booked: isDark ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-50 text-emerald-700',
    available: isDark ? 'bg-sky-900/40 text-sky-200' : 'bg-sky-50 text-sky-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${tones[tone] || ''}`}>
      <span className="font-semibold">{value}</span>
      <span className="opacity-80">{label}</span>
    </span>
  );
};

const AISetupList = ({
  searchId,
  companyId,
  isDark,
  onCreateNew,
  onUseSetup,
  onViewSetup,
  refreshKey,
  activeSetupId = null,
  compact = false,
}) => {
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSetupId, setSelectedSetupId] = useState('');

  const load = useCallback(async () => {
    if (!searchId) return;
    setLoading(true);
    setError('');
    try {
      const qs = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
      const data = await aiAutomationApi.get(`/interviews/search/${encodeURIComponent(searchId)}/setups${qs}`);
      setSetups(Array.isArray(data?.setups) ? data.setups : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.detail || err.message || 'Failed to load interview setups.');
    } finally {
      setLoading(false);
    }
  }, [searchId, companyId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const nextId = activeSetupId != null ? String(activeSetupId) : '';
    setSelectedSetupId(nextId);
  }, [activeSetupId]);

  const selectedSetup = useMemo(() => setups.find((setup) => String(setup.setup_id) === String(selectedSetupId)) || null, [setups, selectedSetupId]);

  const panel = `rounded-lg border p-4 ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`;
  const heading = `text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`;
  const sub = `text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className={heading}>AI interview setups</h3>
          <p className={sub}>Reusable configurations for this job. Each keeps its own slots and invited candidates.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {setups.length > 0 && (
            <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-gray-600 bg-gray-900 text-gray-200' : 'border-gray-300 bg-white text-gray-700'}`}>
              <span className="whitespace-nowrap font-semibold">Saved setup</span>
              <select
                value={selectedSetupId || ''}
                onChange={(event) => {
                  const nextId = String(event.target.value || '');
                  setSelectedSetupId(nextId);
                  const nextSetup = setups.find((setup) => String(setup.setup_id) === nextId);
                  if (nextSetup && typeof onUseSetup === 'function') {
                    onUseSetup(nextSetup);
                  }
                }}
                className={`min-w-[220px] rounded-md border px-2 py-1.5 text-xs outline-none ${isDark ? 'border-gray-600 bg-gray-900 text-gray-200' : 'border-gray-300 bg-white text-gray-800'}`}
              >
                <option value="">Select a saved setup</option>
                {setups.map((setup) => (
                  <option key={setup.setup_id} value={String(setup.setup_id)}>
                    {setup.display_name || setup.setup_name || setup.name || 'AI Interview'}
                    {setup.duration_minutes != null ? ` · ${setup.duration_minutes} min` : ''}
                    {setup.locked ? ' · locked' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!compact && typeof onCreateNew === 'function' && (
            <button type="button" onClick={onCreateNew}
              className={`rounded-lg border px-3 py-2 text-xs font-semibold ${isDark ? 'border-indigo-500 text-indigo-200 hover:bg-indigo-900/30' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'}`}>
              + Create new setup
            </button>
          )}
        </div>
      </div>

      {selectedSetup && !compact && (
        <div className={`rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-indigo-900 bg-indigo-950/30 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-800'}`}>
          Selected: <span className="font-semibold">{selectedSetup.display_name || selectedSetup.setup_name || selectedSetup.name || 'AI Interview'}</span>
          {selectedSetup.duration_minutes != null && <> · {selectedSetup.duration_minutes} min</>}
          {selectedSetup.locked && <> · locked</>}
        </div>
      )}

      {loading && <p className={sub}>Loading setups…</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {!loading && !error && setups.length === 0 && (
        <div className={panel}><p className={sub}>No AI interview setups yet. Create one to start scheduling.</p></div>
      )}

      {!compact && setups.map((s) => (
        <div key={s.setup_id} className={`${panel} ${String(s.setup_id) === String(activeSetupId || selectedSetupId) ? (isDark ? 'border-indigo-500 ring-1 ring-indigo-500/30' : 'border-indigo-400 ring-1 ring-indigo-200') : ''}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className={heading}>{s.display_name || s.setup_name || s.name || 'AI Interview'}</p>
                {String(s.setup_id) === String(activeSetupId || selectedSetupId) && (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-indigo-900/40 text-indigo-200' : 'bg-indigo-100 text-indigo-700'}`}>
                    Selected
                  </span>
                )}
                {s.locked && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${isDark ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-800'}`}
                    title="Candidates already invited — this setup is locked (immutable).">🔒 Locked</span>
                )}
              </div>
              <p className={`mt-1 ${sub}`}>
                {(s.mode || 'ai').toUpperCase()}
                {s.duration_minutes != null && <> · {s.duration_minutes} min</>}
                {s.total_questions != null && <> · {s.total_questions} questions</>}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <StatPill label="invited" value={s.stats?.invited ?? 0} isDark={isDark} tone="invited" />
                <StatPill label="booked" value={s.stats?.booked ?? 0} isDark={isDark} tone="booked" />
                <StatPill label="slots free" value={s.stats?.available ?? 0} isDark={isDark} tone="available" />
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2">
              {typeof onUseSetup === 'function' && (
                <button type="button" onClick={() => {
                  setSelectedSetupId(String(s.setup_id));
                  onUseSetup(s);
                }}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold text-white ${isDark ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                  {s.locked ? 'Invite more candidates' : 'Use this setup'}
                </button>
              )}
              {typeof onViewSetup === 'function' && (
                <button type="button" onClick={() => onViewSetup(s)}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  View details
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AISetupList;
